import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
    PROFILE_STORES_AI_OFFER_RECOMMENDATIONS,
    PROFILE_STORES_OFFERS,
    PROFILE_STORES_OFFERS_RETENTION_BUCKETS
} from '../../../shared/constants/endpoints';
import './DraftNudgeBanner.css';

// CP-111: make offer-publishing inaction visible where the merchant actually
// lives — the Appointments home. Unpublished AI drafts are invisible to
// customers; this banner is the cost of that inaction, made legible.
// Renders nothing when there are no waiting drafts (it's a nag, not a fixture).
const WINBACK = ['AT_RISK', 'SLIPPING_AWAY', 'DORMANT'];
const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(value);

const DraftNudgeBanner = ({ request, onReviewDrafts }) => {
    const [draftsWaiting, setDraftsWaiting] = useState(0);
    const [unNudged, setUnNudged] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [draftsRes, bucketsRes, offersRes] = await Promise.all([
                    request(PROFILE_STORES_AI_OFFER_RECOMMENDATIONS, { method: 'GET', requestName: 'draft-nudge-drafts' }),
                    request(PROFILE_STORES_OFFERS_RETENTION_BUCKETS, { method: 'GET', requestName: 'draft-nudge-buckets' }).catch(() => null),
                    request(PROFILE_STORES_OFFERS, { method: 'GET', requestName: 'draft-nudge-offers' }).catch(() => null)
                ]);
                if (cancelled) return;
                const recommendations = Array.isArray(draftsRes?.data?.recommendations)
                    ? draftsRes.data.recommendations
                    : Array.isArray(draftsRes?.data) ? draftsRes.data : [];
                const waiting = recommendations.filter(
                    (rec) => !(rec?.isPublished || rec?.status === 'PUBLISHED')
                ).length;
                setDraftsWaiting(waiting);

                const liveTargets = new Set((offersRes?.data?.offers || offersRes?.data || [])
                    .filter((offer) => offer?.lifecycle === 'live' && offer?.targetRetentionBucket)
                    .map((offer) => String(offer.targetRetentionBucket).toUpperCase()));
                const buckets = bucketsRes?.data?.buckets || [];
                setUnNudged(buckets
                    .filter((bucket) => WINBACK.includes(String(bucket?.bucket).toUpperCase())
                        && Number(bucket?.count) > 0
                        && !liveTargets.has(String(bucket?.bucket).toUpperCase()))
                    .reduce((sum, bucket) => sum + Number(bucket?.count || 0), 0));
            } catch (error) {
                // Additive nag — a failed fetch just hides it.
            }
        })();
        return () => { cancelled = true; };
    }, [request]);

    if (draftsWaiting === 0) return null;
    return (
        <div className="draft-nudge-banner" role="status">
            <Sparkles size={16} aria-hidden="true" />
            <p>
                <strong>{draftsWaiting} AI offer{draftsWaiting === 1 ? '' : 's'} drafted but not published.</strong>
                {unNudged > 0
                    ? ` ${formatNumber(unNudged)} customers in win-back buckets have no live offer aimed at them.`
                    : ' Publish them before the next AI run replaces them.'}
            </p>
            <button type="button" className="btn small primary" onClick={onReviewDrafts}>
                Review drafts
            </button>
        </div>
    );
};

export default DraftNudgeBanner;
